import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import NewsCard from '@/components/feature/NewsCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Pagination from '@/components/feature/Pagination';
import { allTags, getArticlesByCategory, getCategory } from '@/utils/news';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryName } = useParams<{ categoryName: string }>();
  const category = getCategory(categoryName);
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [tag, setTag] = useState<string>('');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setSort('latest');
    setTag('');
    setPage(1);
  }, [categoryName]);

  useSeo({
    title: category ? `${category.name} - 9·1牛网资讯频道` : '资讯频道 - 9·1牛网',
    description: category
      ? `${category.intro}`
      : '9·1牛网资讯频道，为你提供多领域的最新资讯内容。',
    keywords: category ? `${category.name},9·1牛网,${category.description}` : '9·1牛网,资讯频道',
  });

  const filtered = useMemo(() => {
    if (!category) return [];
    let list = getArticlesByCategory(category.key);
    if (tag) list = list.filter((a) => a.tags.includes(tag));
    list.sort((a, b) => {
      if (sort === 'hot') return b.readCount - a.readCount;
      return new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime();
    });
    return list;
  }, [category, tag, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 w-full px-4 md:px-8 py-20 flex flex-col items-center justify-center text-center">
          <i className="ri-error-warning-line text-4xl text-accent-500 mb-4" />
          <h1 className="font-heading font-bold text-xl text-foreground-950">未找到该分类</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的资讯频道不存在或已下线。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" /> 返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const resetFilters = () => {
    setTag('');
    setSort('latest');
    setPage(1);
  };

  const tagList = ['全部', ...allTags];
  const activeTags = allTags.filter((t) => getArticlesByCategory(category.key).some((a) => a.tags.includes(t)));

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: `${category.name}频道` }]} />

        <header className="mt-4 mb-6 md:mb-8">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
                {category.name}频道
              </h1>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">{category.description}</p>
            </div>
          </div>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">{category.intro}</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
          <aside className="lg:col-span-1">
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="lg:hidden w-full flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm font-medium text-foreground-800 cursor-pointer"
            >
              <span className="inline-flex items-center gap-2">
                <i className="ri-filter-3-line" /> 筛选与排序
              </span>
              <i className={filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
            </button>

            <div className={`${filterOpen ? 'block' : 'hidden'} lg:block mt-3 lg:mt-0 space-y-5 p-4 lg:p-5 rounded-lg bg-background-100 border border-background-200`}>
              <div>
                <h3 className="text-sm font-bold text-foreground-900 mb-3">排序方式</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'latest', label: '最新发布' },
                    { key: 'hot', label: '最多阅读' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        setSort(opt.key as 'latest' | 'hot');
                        setPage(1);
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        sort === opt.key
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-200 text-foreground-700 hover:bg-background-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-900 mb-3">标签筛选</h3>
                <div className="flex flex-wrap gap-2">
                  {tagList.map((t) => {
                    const value = t === '全部' ? '' : t;
                    const disabled = t !== '全部' && !activeTags.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                          setTag(value);
                          setPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                          tag === value
                            ? 'bg-accent-500 text-background-50'
                            : 'bg-background-200 text-foreground-700 hover:bg-background-300'
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                onClick={resetFilters}
                className="w-full h-9 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line align-middle mr-1" /> 重置筛选
              </button>
            </div>
          </aside>

          <section className="lg:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-foreground-500">
                共 <span className="text-primary-600 font-medium">{filtered.length}</span> 篇文章
              </p>
            </div>

            {pageItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 rounded-lg border border-dashed border-background-300 bg-background-100 text-center">
                <i className="ri-file-search-line text-4xl text-foreground-400 mb-3" />
                <p className="text-base font-medium text-foreground-800">未找到相关文章</p>
                <p className="text-sm text-foreground-500 mt-1">换个标签或重置筛选试试</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  重置筛选
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {pageItems.map((article) => (
                  <NewsCard key={article.id} article={article} variant="row" />
                ))}
              </div>
            )}

            <Pagination current={page} total={totalPages} onChange={setPage} />
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}